/**
 * Events: v1 → v2 (decision 0036, spec §2.9): all 81 v1 tags. Every envelope also maps its actor and `approvedBy.by`,
 * and every RecordMeta inside the payload. Upcast events keep their stored `v: 1`; evolve uses it to leave v2-only
 * bookkeeping (specRev, startedAt/closedAt of tasks and intake, overdueNoticed) alone.
 */
import type { EventMeta, ProjectEvent, ProjectEventEnvelope } from "../events"
import type * as V1 from "../legacy/v1/index"
import { fromV1CommandRejection, fromV1ProposalBody } from "./commands"
import {
  fromV1Actor,
  fromV1EntityRef,
  fromV1Meta,
  fromV1Mind,
  fromV1Placement,
  fromV1RankedRef,
  fromV1Unmet,
  itemId,
} from "./common"
import {
  executorToAssignee,
  fromV1BlockReason,
  fromV1Edge,
  fromV1Initiative,
  fromV1Intake,
  fromV1Shape,
  fromV1Task,
  fromV1TaskResolution,
  fromV1TaskStatus,
  intakeClosure,
} from "./item"
import { fromV1Policy, fromV1PolicyDocument } from "./policy"
import {
  fromV1Agent,
  fromV1Area,
  fromV1Artifact,
  fromV1Comment,
  fromV1Goal,
  fromV1JsonPatch,
  fromV1Kpi,
  fromV1MemoryItem,
  fromV1Project,
  fromV1Run,
  fromV1RunOutcome,
} from "./records"

type V1Event = V1.ProjectEvent
type V1StatusCause = typeof V1.StatusCause.Type
type StatusCause = Extract<ProjectEvent, { _tag: "ItemStatusChanged" }>["cause"]
type V1Proposal = V1.Proposal

export const fromV1StatusCause = (c: V1StatusCause): StatusCause => c

export const fromV1Proposal = (
  p: V1Proposal,
): Extract<ProjectEvent, { _tag: "ProposalOpened" }>["proposal"] => {
  const m = fromV1Meta(p)
  return {
    ...m,
    body: fromV1ProposalBody(p.body),
    proposedBy: fromV1Actor(p.proposedBy),
    blocks: p.blocks.map(fromV1EntityRef),
    resolution: p.resolution === null ? null : { ...p.resolution, by: fromV1Actor(p.resolution.by) },
  }
}

/**
 * One v1 event as v2. `seq` is the event's seq (an intake capture's ref and contentSeq come from it).
 * Exhaustive over the 81 v1 tags.
 */
export const fromV1Event = (e: V1Event, seq: number): ProjectEvent => {
  switch (e._tag) {
    // ---- genesis, settings, sync ----
    case "ProjectCreated":
      return { _tag: "ProjectCreated", project: fromV1Project(e.project) }
    case "ProjectUpdated":
      return { _tag: "ProjectUpdated", patch: fromV1JsonPatch(e.patch), before: fromV1JsonPatch(e.before) }
    case "WorkspaceSynced":
      return e
    case "PolicyCreated":
      return { _tag: "PolicyCreated", policy: fromV1Policy(e.policy) }
    case "PolicyRevised":
      return {
        ...e,
        document: fromV1PolicyDocument(e.document),
        before: fromV1PolicyDocument(e.before),
      }
    // ---- areas and agents ----
    case "AreaCreated":
      return { _tag: "AreaCreated", area: fromV1Area(e.area) }
    case "AreaUpdated":
      return {
        _tag: "AreaUpdated",
        areaId: e.areaId,
        patch: fromV1JsonPatch(e.patch) as never,
        before: fromV1JsonPatch(e.before) as never,
      }
    case "AreaCharterRevised":
    case "AreaArchived":
    case "IntegrationGranted":
    case "IntegrationRevoked":
      return e
    case "AgentHired":
      return { _tag: "AgentHired", agent: fromV1Agent(e.agent) }
    case "AgentUpdated":
    case "AgentCharterRevised":
    case "AgentStatusChanged":
      return e
    case "AgentMindChanged":
      return { ...e, mind: fromV1Mind(e.mind), before: fromV1Mind(e.before) }
    // ---- goals and KPIs ----
    case "GoalCreated":
      return { _tag: "GoalCreated", goal: fromV1Goal(e.goal) }
    case "GoalUpdated":
    case "GoalStatusChanged":
      return e
    case "TargetChanged":
      return {
        _tag: "TargetChanged",
        subject: e.subject._tag === "goal" ? e.subject : { _tag: "item", id: itemId(e.subject.id) },
        condition: e.condition,
        before: e.before,
      }
    case "KpiDefined":
      return { _tag: "KpiDefined", kpi: fromV1Kpi(e.kpi) }
    case "KpiUpdated":
    case "KpiSampleRecorded":
      return e
    // ---- initiatives → top-level items ----
    case "InitiativeCreated":
      return { _tag: "ItemCreated", item: fromV1Initiative(e.initiative) }
    case "InitiativeUpdated": {
      const shape = (s: typeof e.patch.shape) => (s === undefined ? {} : { shape: fromV1Shape(s) })
      const { shape: ps, ...patch } = e.patch
      const { shape: bs, ...before } = e.before
      return {
        _tag: "LegacyInitiativeUpdated",
        itemId: itemId(e.initiativeId),
        patch: { ...patch, ...shape(ps) },
        before: { ...before, ...shape(bs) },
      }
    }
    case "InitiativeStatusChanged":
      return {
        _tag: "LegacyInitiativeStatusChanged",
        itemId: itemId(e.initiativeId),
        status: e.status,
        before: e.before,
        reason: e.reason,
        workDone: e.workDone,
      }
    case "BetVerdictRecorded":
      return {
        _tag: "BetVerdictRecorded",
        itemId: itemId(e.initiativeId),
        value: e.value,
        by: e.by,
        evidence: e.evidence,
      }
    // ---- tasks → items ----
    case "TaskCreated":
      return { _tag: "ItemCreated", item: fromV1Task(e.task) }
    case "TaskUpdated": {
      const map = (p: typeof e.patch) => {
        const { executor, placement, ...rest } = p
        return {
          ...rest,
          ...(placement !== undefined ? { placement: fromV1Placement(placement) } : {}),
          ...(executor !== undefined ? { assignee: executorToAssignee(executor) } : {}),
        }
      }
      return { _tag: "ItemUpdated", itemId: itemId(e.taskId), patch: map(e.patch), before: map(e.before) }
    }
    case "ContractRevised":
      return {
        _tag: "ChecksRevised",
        itemId: itemId(e.taskId),
        checks: e.checks,
        review: e.review,
        before: e.before,
      }
    case "TaskAssigned":
      return {
        _tag: "ItemAssigned",
        itemId: itemId(e.taskId),
        owner: e.assigneeId === null ? null : { _tag: "agent", agentId: e.assigneeId },
        before: e.before === null ? null : { _tag: "agent", agentId: e.before },
      }
    case "TaskStatusChanged":
      return {
        _tag: "ItemStatusChanged",
        itemId: itemId(e.taskId),
        from: fromV1TaskStatus(e.from),
        to: fromV1TaskStatus(e.to),
        cause: fromV1StatusCause(e.cause),
        blocked: e.blocked === null ? null : fromV1BlockReason(e.blocked),
        resolution: fromV1TaskResolution(e.to),
      }
    case "TaskPaused": {
      const { taskId, ...rest } = e
      return { ...rest, _tag: "ItemPaused", itemId: itemId(taskId) }
    }
    case "RequestDecided":
      return { _tag: "RequestDecided", itemId: itemId(e.taskId), accepted: e.accepted, note: e.note }
    case "ReviewRecorded": {
      const { taskId, ...rest } = e
      return { ...rest, itemId: itemId(taskId) }
    }
    case "RankChanged":
      return { ...e, ref: fromV1RankedRef(e.ref) }
    // ---- edges, holds, plan versions ----
    case "EdgeAdded":
      return { _tag: "EdgeAdded", edge: fromV1Edge(e.edge) }
    case "EdgeRemoved":
      return { _tag: "EdgeRemoved", edgeId: e.edgeId, edge: fromV1Edge(e.edge) }
    case "HoldSet":
      return { ...e, ref: fromV1EntityRef(e.ref) }
    case "ChangesetApplied":
      return e
    // ---- proposal ledger ----
    case "ProposalOpened":
      return { _tag: "ProposalOpened", proposal: fromV1Proposal(e.proposal) }
    case "ProposalRevised":
      return { ...e, body: fromV1ProposalBody(e.body) }
    case "ProposalApplyFailed":
      return { ...e, rejection: fromV1CommandRejection(e.rejection) }
    case "ProposalRevisionRequested":
    case "ProposalApproved":
    case "ProposalApplied":
    case "ProposalRejected":
    case "ProposalAnswered":
    case "ProposalExpired":
    case "ProposalWithdrawn":
    case "ProposalAdopted":
      return e
    // ---- comments, the agent bus, artifacts ----
    case "CommentPosted":
      return { _tag: "CommentPosted", comment: fromV1Comment(e.comment) }
    case "CommentEdited":
    case "ThreadResolved":
    case "AskStateChanged":
      return e
    case "ArtifactPosted":
      return { _tag: "ArtifactPosted", artifact: fromV1Artifact(e.artifact) }
    case "ArtifactStateChanged":
      return e
    // ---- runs ----
    case "RunRequested":
      return { ...e, run: fromV1Run(e.run) }
    case "RunWaiting":
      return { ...e, unmet: e.unmet.map(fromV1Unmet) }
    case "RunOutcomeReported":
      return { ...e, outcome: fromV1RunOutcome(e.outcome) }
    case "RunEnded":
      return {
        ...e,
        claimed: e.claimed === null ? null : fromV1RunOutcome(e.claimed),
        outcome: fromV1RunOutcome(e.outcome),
      }
    case "RunLeased":
    case "RunRequeued":
    case "RunStarted":
    case "RunMilestone":
    case "RunVerdictReported":
    case "RunTaintRaised":
    case "RunCancelRequested":
    case "OutwardActionExecuted":
    case "OutwardActionFailed":
      return e
    // ---- intake → triage items ----
    case "IntakeCaptured":
      return { _tag: "ItemCreated", item: fromV1Intake(e.intake, seq) }
    case "IntakeTriaged":
      return {
        _tag: "ItemUpdated",
        itemId: itemId(e.intakeId),
        patch: { areaId: e.suggestedAreaId },
        before: { areaId: e.before },
      }
    case "IntakeResolved": {
      const closure = intakeClosure(e.status) ?? {
        resolution: { _tag: "wont_do" as const },
        reason: "closed",
      }
      return {
        _tag: "ItemStatusChanged",
        itemId: itemId(e.intakeId),
        from: "triage",
        to: "cancelled",
        cause: { _tag: "cancelled", reason: closure.reason },
        blocked: null,
        resolution: closure.resolution,
      }
    }
    // ---- memory ----
    case "MemoryItemCreated":
      return { _tag: "MemoryItemCreated", item: fromV1MemoryItem(e.item) }
    case "MemoryItemRevised":
      return { ...e, item: fromV1MemoryItem(e.item) }
    case "MemoryItemArchived":
    case "MemoryItemRevoked":
      return e
    // ---- watchdog, outcomes, privacy ----
    case "StallDetected":
      return { ...e, subject: fromV1EntityRef(e.subject) }
    case "OutcomeCheckScheduled":
      return { ...e, subject: fromV1EntityRef(e.subject) }
    case "OutcomeCheckDue":
      return { _tag: "OutcomeCheckDue", subject: fromV1EntityRef(e.subject) }
    case "Redacted": {
      const { refs, ...rest } = e
      return refs === undefined ? rest : { ...rest, refs: refs.map(fromV1EntityRef) }
    }
    default:
      return e satisfies never
  }
}

/** The envelope's metadata as v2: actors mapped; `v` stays as stored (1). */
export const fromV1EventMeta = (m: typeof V1.EventMeta.Type): EventMeta => ({
  ...m,
  actor: fromV1Actor(m.actor),
  approvedBy: m.approvedBy === null ? null : { ...m.approvedBy, by: fromV1Actor(m.approvedBy.by) },
})

export const fromV1EventEnvelope = (e: V1.ProjectEventEnvelope): ProjectEventEnvelope => {
  const { event, ...meta } = e
  return { ...fromV1EventMeta(meta), event: fromV1Event(event, meta.seq) }
}
